import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';

import { Box, CircularProgress, Paper, Typography } from '@mui/material';

import { JSON_HIGHLIGHT_COLORS, parseJsonHighlights } from '~/utils/societePayloadBuilder/highlightJson';
import type { PayloadBuilderState } from '~/utils/societePayloadBuilder/types';
import { serializePreviewPayload } from '~/utils/societePayloadBuilder/validatePayload';

import { PAYLOAD_BUILDER_MONO_FONT } from './PayloadBuilderShared';

interface PayloadBuilderPreviewProps {
  state: PayloadBuilderState;
  borderSoft: string;
  maxHeight: string;
}

export default function PayloadBuilderPreview({ state, borderSoft, maxHeight }: PayloadBuilderPreviewProps) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsReady(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const deferredState = useDeferredValue(state);
  const isUpdating = isReady && deferredState !== state;

  const previewJson = useMemo(() => {
    if (!isReady) return '';
    return serializePreviewPayload(deferredState);
  }, [deferredState, isReady]);

  const segments = useMemo(() => (previewJson ? parseJsonHighlights(previewJson) : []), [previewJson]);

  return (
    <Paper
      elevation={0}
      sx={{
        position: { xs: 'static', md: 'sticky' },
        top: { md: 72 },
        height: { xs: 'auto', md: maxHeight },
        maxHeight: { xs: 420, md: maxHeight },
        minHeight: { xs: 280, md: 360 },
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
        bgcolor: '#111827',
        color: '#f9fafb',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          fontFamily: PAYLOAD_BUILDER_MONO_FONT,
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          position: 'relative',
        }}
      >
        {!isReady || segments.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              minHeight: 120,
              color: 'grey.500',
            }}
          >
            <CircularProgress size={18} color="inherit" />
            <Typography variant="caption">Loading preview…</Typography>
          </Box>
        ) : (
          <code style={{ opacity: isUpdating ? 0.65 : 1, transition: 'opacity 0.15s ease' }}>
            {segments.map((segment, index) =>
              segment.kind === 'plain' ? (
                <React.Fragment key={index}>{segment.text}</React.Fragment>
              ) : (
                <Box component="span" key={index} sx={{ color: JSON_HIGHLIGHT_COLORS[segment.kind] }}>
                  {segment.text}
                </Box>
              ),
            )}
          </code>
        )}
      </Box>
    </Paper>
  );
}
